import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import { PDFDocument } from 'pdf-lib'
import { pdf } from '@react-pdf/renderer'
import ReactGA from 'react-ga4'

import { bindActionCreators } from 'redux'
import DownloadPdfButton from '@components/inputs/button/downloadPdfButton'
import LoadingModal from '@components/loading/loadingModal'
import AutoGrowTextarea from '@components/inputs/textArea'
import MyDocument from '@generators/pdf'
import BeneficiaryInfo from '@generators/htmlComponents/BeneficiaryInfo'
import { Line } from '@generators//htmlComponents/Line'
import { MainTitle } from '@generators/htmlComponents/MainTitle'
import { ActionTitle } from '@generators/htmlComponents/ActionTitle'
import { RiskClassification } from '@generators/htmlComponents/RiskClassification'
import { Important } from '@generators/htmlComponents/Important'
import { HealthCharacteristic } from '@generators/htmlComponents/HealthCharacteristic'
import { BloodPressure } from '@generators/htmlComponents/BloodPressure'
import { CongratulationsText } from '@generators/htmlComponents/CongratulationsText'
import { Footer } from '@generators/htmlComponents/Footer'
import { SecondaryTitle } from '@generators/htmlComponents/SecondaryTitle'
import { NoRiskRecommendation } from '@generators/htmlComponents/NoRiskRecommendation'
import InfoLine from '@generators/htmlComponents/InfoLine'
import Info from '@generators/htmlComponents/icons/Info'
import AnthropometricMeasures from '@generators/htmlComponents/AnthropometricMeasures'
import DownloadPdfModal from '@components/downloadPdfModal'
import ArrowBack from '@components/arrowBack'
import Loading from '@components/loading'
import InformedConsent from '@generators/informedConsent'
import showToast from '@util/ToastUtil'
import colors from '@settings/styleSettings'
import { getRecommendations } from '@util/recommendationToHtmlUtil'
import { getDateFromStringDate } from '@util/timeUtil'
import {
  getHdlC,
  getIfIsDiabetic,
  getIfIsDyslipidemic,
  getIfIsHypertensive,
  getImc,
  getRcq,
  getBodyWater,
  getBoneMassEstimative,
  getProtein,
} from '@util/equationsUtil'
import { cpfShowMask, insuranceMask, oneDecimalMask } from '@util/stringUtil'
import sexEnum from '@util/sexEnum'
import InsurerEnum from '@util/InsurerEnum'
import { base64ToArrayBuffer } from '@util/generalUtil'
import ExamTypesEnum from '@util/ExamTypesEnum'

import {
  Page, Document, LoadingContainer, ArrowArea
} from './styles'
import { getReportToInsurance as getReportToInsuranceAction } from '@actions/beneficiary'

const styles = {
  section: {
    margin: '0 0',
  },
  firstPart: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
  },
}

const ReportResult = ({
  reportDataByBeneficiary,
  reportDataByUser,
  loggedIn,
  getReportToInsurance,
}) => {
  const reportData = reportDataByBeneficiary || reportDataByUser
  const {
    name,
    age,
    insuranceNumber,
    sex,
    birthdate,
    cpf,
    lastGeneralCollection,
    assign,
  } = reportData || {}

  useEffect(() => {
    if (lastGeneralCollection) {
      console.log('⚕️ lastGeneralCollection:', lastGeneralCollection)
    }
  }, [lastGeneralCollection])

  const {
    collectedAt,
    height,
    weight,
    abdominalCircumference,
    waistCircumference,
    hipCircumference,
    fat,
    visceralFat,
    leanMass,
    calories,
    triglycerides,
    glucose,
    hdlCholesterol,
    ldlCholesterol,
    systBloodPressure,
    diastBloodPressure,
    hasSystemicArterialHypertension,
    hasDiabetes,
    totalCholesterol,
    notHdl,
    vldlCholesterol,
    observation,
  } = lastGeneralCollection || {}

  console.log(
    '🧪 lab values →',
    {
      glucose,
      hdlCholesterol,
      ldlCholesterol,
      notHdl,
      totalCholesterol,
      triglycerides,
      vldlCholesterol
    }
  )

  const calculatedMetrics = useMemo(() => {
    if (!weight || !height) return {}

    let water = 0
    let bone = 0
    let protein = 0

    if (leanMass && Number(leanMass) > 0) {
      water = Number(getBodyWater(leanMass, weight))
      bone = Number(getBoneMassEstimative(leanMass))
      protein = Number(getProtein(leanMass, weight))
    }

    return {
      bodyWater: water,
      boneMass: bone,
      protein
    }
  }, [weight, height, leanMass])

  const navigate = useNavigate()
  const { beneficiaryId, collectionId } = useParams()
  const insurer = InsurerEnum['unimed-assis']

  const imc = Number(getImc(weight, height))
  const rcq = Number(getRcq(waistCircumference, hipCircumference))

  const hdlC = notHdl || getHdlC(totalCholesterol, hdlCholesterol)
  const isHypertensive = getIfIsHypertensive(
    systBloodPressure,
    diastBloodPressure,
    hasSystemicArterialHypertension
  )
  const isDiabetic = getIfIsDiabetic(glucose, hasDiabetes)

  const isDyslipidemic = getIfIsDyslipidemic(
    totalCholesterol,
    hdlCholesterol,
    triglycerides,
    ldlCholesterol
  )

  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [mergeTermsOfConsent, setMergeTermsOfConsent] = useState(false)
  const [mergeLaboratoryExams, setMergeLaboratoryExams] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (reportData === null && !loggedIn) {
      goBack()
    } else if (reportData === null) {
      setLoading(true)
      getReportToInsurance({ id: beneficiaryId, collectionId }, () => setLoading(false))
    }
  }, [])

  const renderLoading = () => (
    <LoadingContainer>
      <LoadingModal />
    </LoadingContainer>
  )

  if (!reportData) return renderLoading()
  const goBack = () => {
    navigate(-1)
  }

  const onExportToPdf = async () => {
    setShowDownloadModal(true)
  }

  const mergePdf = async (mergedPDFBlob, examName, I) => {
    if (reportData[examName]) {
      try {
        const localBlob = new Blob(
          [base64ToArrayBuffer(reportData[examName][I])],
          { type: 'application/pdf' }
        )
        return await mergePDFs(localBlob, mergedPDFBlob)
      } catch (error) {
        showToast('error', 'Ocorreu um erro ao buscar o exame do beneficiário.')
      }
    }
    return mergedPDFBlob
  }

  const mergePdfList = async (mergedPDFBlob, pdfList) => {
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const index in reportData[pdfList]) {
      // eslint-disable-next-line no-param-reassign, no-await-in-loop
      mergedPDFBlob = await mergePdf(mergedPDFBlob, pdfList, index)
    }
    return mergedPDFBlob
  }

  const onDownloadPdf = async (selected) => {
    setLoading(true)
    ReactGA.event({
      category: 'Exportar PDF',
      action: `Clique que em Exportar PDF - ${selected}`,
      label: 'Botão de exportar pdf ',
    })
    if (reportData) {
      const reportBlob = await pdf(
        <MyDocument
          reportData={reportData}
          insurance="unimed-assis"
        />
      )
      let termsOfConsentBlob
      if (assign) {
        termsOfConsentBlob = await pdf(
          <InformedConsent
            beneficiary={reportData}
            insurance="unimed-assis"
          />
        )
      }

      const reportPdf = await reportBlob.toBlob()
      const termsOfConsentPdf = termsOfConsentBlob && (await termsOfConsentBlob.toBlob())
      let mergedPDFBlob = reportPdf
      if (termsOfConsentPdf && mergeTermsOfConsent) {
        mergedPDFBlob = await mergePDFs(termsOfConsentPdf, reportPdf)
      }

      if (mergeLaboratoryExams) {
        mergedPDFBlob = await mergePdfList(mergedPDFBlob, ExamTypesEnum.Laboratorial.name)
        mergedPDFBlob = await mergePdfList(mergedPDFBlob, ExamTypesEnum.Others.name)
      }
      const pdfWindow = window.open()
      pdfWindow.location.href = URL.createObjectURL(mergedPDFBlob)
      setLoading(false)

    }
  }

  async function mergePDFs(localPdfBlob, examPdfBlob) {
    if (!localPdfBlob) return examPdfBlob
    const localPDFArrayBuffer = await localPdfBlob.arrayBuffer()
    const myPDFArrayBuffer = await examPdfBlob.arrayBuffer()

    const localPDFDoc = await PDFDocument.load(localPDFArrayBuffer)
    const myPDFDoc = await PDFDocument.load(myPDFArrayBuffer)

    const mergedPDFDoc = await PDFDocument.create()
    const [localPDFPages, myPDFPages] = await Promise.all([
      mergedPDFDoc.copyPages(myPDFDoc, myPDFDoc.getPageIndices()),
      mergedPDFDoc.copyPages(localPDFDoc, localPDFDoc.getPageIndices()),
    ])

    localPDFPages.forEach((page) => mergedPDFDoc.addPage(page))
    myPDFPages.forEach((page) => mergedPDFDoc.addPage(page))
    const mergedPDFBytes = await mergedPDFDoc.save()
    const mergedPDFBlob = new Blob([mergedPDFBytes], {
      type: 'application/pdf',
    })

    return mergedPDFBlob
  }

  const info = (color) => <Info color={color} />

  const {
    imediate, nextMonths, nextYears, stageOne, stageTwo
  } = getRecommendations(reportData || {})

  function renderHealthCharacteristic() {
    return (
      <>
        <Line lineColor={colors.softGray} style={{ margin: '32 0 12 0' }} />
        <div style={styles.section}>
          <HealthCharacteristic type="imc" value={Number(imc)} />
          {waistCircumference > 0 && hipCircumference > 0 && (
            <HealthCharacteristic type="rcq" value={Number(rcq)} />
          )}
        </div>
      </>
    )
  }

  function renderNutritionalAssessment() {
    return (
      <div style={styles.section}>
        <SecondaryTitle text="Aferição nutricional" />
        {fat && <HealthCharacteristic type="fat" value={Number(fat)} />}
        {visceralFat && (
          <HealthCharacteristic type="visceralFat" value={Number(visceralFat)} />
        )}
        {leanMass && (
          <HealthCharacteristic type="muscleMass" value={Number(leanMass)} />
        )}
        {calories && <HealthCharacteristic type="calories" value={Number(calories)} />}
        {Number(calculatedMetrics.bodyWater) > 0 && (
        <HealthCharacteristic type="bodyWater" value={Number(calculatedMetrics.bodyWater)} />
        )}
        {Number(calculatedMetrics.boneMass) > 0 && (
        <HealthCharacteristic type="boneMass" value={Number(calculatedMetrics.boneMass)} />
        )}
        {Number(calculatedMetrics.protein) > 0 && (
        <HealthCharacteristic type="protein" value={Number(calculatedMetrics.protein)} />
        )}
      </div>
    )
  }

  function renderBloodPressure() {
    return (
      <div style={styles.section}>
        <SecondaryTitle text="Pressão arterial" />
        <BloodPressure syst={systBloodPressure} diast={diastBloodPressure} />
        <InfoLine
          text="Beneficiário com hipertensão informada no momento da coleta dos dados?"
          informed={hasSystemicArterialHypertension}
        />
      </div>
    )
  }

  function renderLaboratoryTests() {
    console.log('🔍 renderLaboratoryTests vldlCholesterol:', vldlCholesterol)
    return (
      <div style={styles.section}>
        <SecondaryTitle text="Exames laboratoriais" />
        <InfoLine
          text="Beneficiário com diabetes mellitus informada no momento da coleta dos dados?"
          informed={hasDiabetes}
        />
        {glucose > 0 && (
          <HealthCharacteristic
            type="glucose"
            value={Number(glucose)}
            lineStyle={{ marginBottom: 24 }}
          />
        )}
        <HealthCharacteristic
          type="totalCholesterol"
          value={Number(totalCholesterol)}
        />
        {hdlCholesterol > 0 && (
          <HealthCharacteristic type="hdl" value={Number(hdlCholesterol)} />
        )}
        {hdlC > 0 && <HealthCharacteristic type="hdl-c" value={Number(hdlC)} />}
        {ldlCholesterol > 0 && (
          <HealthCharacteristic type="ldl" value={Number(ldlCholesterol)} />
        )}
        {vldlCholesterol > 0 && (
        <HealthCharacteristic type="vldlCholesterol" value={Number(vldlCholesterol)} />
        )}
        {triglycerides > 0 && (
          <HealthCharacteristic type="triglycerides" value={Number(triglycerides)} />
        )}
      </div>
    )
  }

  function renderObservation() {
    return (
      <div>
        <AutoGrowTextarea value={observation} disabled />
      </div>
    )
  }

  function renderTreatmentRecommendations() {
    return (
      <>
        <SecondaryTitle text="Recomendações de tratamento" />
        {imediate && imediate.length > 0 && (
          <ActionTitle
            titleText="Ações Imediatas"
            titleColor={colors.strongRed}
            recommendations={imediate}
          />
        )}
      </>
    )
  }

  function renderRecommendationActions() {
    return (
      <>
        {nextMonths && nextMonths.length > 0 && (
          <div style={styles.section}>
            <ActionTitle
              titleText="Ações para os próximos meses"
              titleColor={colors.strongYellow}
              recommendations={nextMonths}
            />
          </div>
        )}
        {nextYears && nextYears.length > 0 && (
          <div style={styles.section}>
            <ActionTitle
              titleText="Ações para os próximos anos"
              titleColor={colors.strongGreen}
              recommendations={nextYears}
            />
          </div>
        )}
        {stageOne && stageOne.length > 0 && (
          <div style={styles.section}>
            <ActionTitle
              titleColor={colors.strongBlue}
              exclamationBackGround="#00995C1A"
              titleText={`Ações complementares com base na idade (acima de ${sexEnum[sex].ageLimit} anos) - Etapa 1`}
              recommendations={stageOne}
            />
          </div>
        )}
        {stageTwo && stageTwo.length > 0 && (
          <div style={styles.section}>
            <ActionTitle
              titleText={`Ações complementares com base na idade (acima de ${sexEnum[sex].ageLimit} anos) - Etapa 2`}
              titleColor={colors.strongBlue}
              recommendations={stageTwo}
            />
          </div>
        )}
        { (imediate && nextMonths && nextYears && stageOne && stageTwo)
          && !imediate.length
          && !nextMonths.length
          && !nextYears.length
          && !stageOne.length
          && !stageTwo.length && <NoRiskRecommendation />}
      </>
    )
  }

  function renderWarning() {
    if (insurer.id !== InsurerEnum['unimed-assis'].id) return null
    return (
      <div style={styles.section}>
        <Important icon={info} date={getDateFromStringDate(collectedAt)} />
        <Line lineColor={colors.softGray} />
      </div>
    )
  }

  return reportData === null ? (
    renderLoading()
  ) : (
    <>
      <Document>
        <ArrowArea>
          <ArrowBack onClick={goBack} />
        </ArrowArea>
        <Page>
          <MainTitle />
          <Line />
          <BeneficiaryInfo
            name={name}
            age={age}
            insuranceNumber={insuranceMask(insuranceNumber)}
            cpf={cpfShowMask(cpf)}
            sex={sex}
            birthdate={birthdate}
            collectedAt={collectedAt}
            insurer={insurer.id}
          />
          <Line />
          <RiskClassification
            has={isHypertensive}
            dm={isDiabetic}
            cholesterol={isDyslipidemic}
            imc={imc}
          />
          <Line />
          <AnthropometricMeasures
            height={oneDecimalMask(height)}
            weight={oneDecimalMask(weight)}
            hipCircumference={oneDecimalMask(hipCircumference)}
            waistCircumference={oneDecimalMask(waistCircumference)}
            abdominalCircumference={oneDecimalMask(abdominalCircumference)}
          />
          {renderHealthCharacteristic()}
          {renderNutritionalAssessment()}
          {renderBloodPressure()}
          {totalCholesterol > 0 && renderLaboratoryTests()}
          {observation && (
            renderObservation()
          )}
          {totalCholesterol > 0 && (
            <>
              {renderTreatmentRecommendations()}
              {renderRecommendationActions()}
            </>
          )}
          {renderWarning()}
          <div style={styles.section}>
            {insurer.id === InsurerEnum['unimed-assis'].id ? <CongratulationsText /> : null}
          </div>
          <div style={styles.section}>
            {insurer.id === InsurerEnum['unimed-assis'].id ? <Footer /> : null}
          </div>
        </Page>
        {loading ? (
          <Loading />
        ) : (
          <DownloadPdfButton onClick={() => onExportToPdf()} />
        )}
      </Document>
      {showDownloadModal && (
        <DownloadPdfModal
          hasLaboratoryExam={!!reportData[ExamTypesEnum.Laboratorial.name]}
          hasAcceptedTerms={assign !== null}
          mergeTermsOfConsent={mergeTermsOfConsent}
          mergeLaboratoryExams={mergeLaboratoryExams}
          onChangeTermsOfConsent={setMergeTermsOfConsent}
          onChangeLaboratoryExams={setMergeLaboratoryExams}
          loading={loading}
          isOpen={showDownloadModal}
          setClose={() => setShowDownloadModal(false)}
          onSubmit={onDownloadPdf}
        />
      )}
    </>
  )
}

ReportResult.propTypes = {
  reportDataByBeneficiary: PropTypes.shape({
    id: PropTypes.number,
    mealPlan: PropTypes.string,
  }),
  reportDataByUser: PropTypes.shape({
    id: PropTypes.number,
    mealPlan: PropTypes.string,
  }),
  getReportToInsurance: PropTypes.func.isRequired,
  loggedIn: PropTypes.bool.isRequired,
}

ReportResult.defaultProps = {
  reportDataByBeneficiary: null,
  reportDataByUser: null,
}

const mapStateToProps = ({ beneficiary, user }) => ({
  reportDataByBeneficiary: beneficiary.reportData,
  reportDataByUser: user.reportData,
  loggedIn: user.loggedIn,
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    getReportToInsurance: getReportToInsuranceAction,
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(ReportResult)
